import { useState } from 'react';
import { Shield, Check, AlertTriangle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { mfaApi, ApiError } from '@solara/api-client';
import type { MfaSetupResponse } from '@solara/api-client';

export default function MfaSetupPage() {
  const { t } = useTranslation('p1');
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin() {
    setError(null);
    setBusy(true);
    try {
      const result = await mfaApi.setup();
      setSetup(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function activate() {
    if (!token) {
      setError(t('mfa.tokenRequired'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await mfaApi.activate(token);
      setBackupCodes(result.backupCodes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('mfa.invalidToken'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('mfa.title')}</h1>
        <p className="text-fg-muted text-sm">{t('mfa.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {!setup && !backupCodes && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {t('mfa.beginEnrolment')}
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p>{t('mfa.beginCopy')}</p>
            <Button onClick={begin} disabled={busy}>
              {busy ? t('mfa.generating') : t('mfa.beginEnrolment')}
            </Button>
          </CardBody>
        </Card>
      )}

      {setup && !backupCodes && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('mfa.step2')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4 text-sm">
            <div>
              <p className="font-medium">{t('mfa.otpauthUri')}</p>
              <code className="block rounded bg-surface-muted px-2 py-1 text-[11px] break-all">
                {setup.otpauthUri}
              </code>
              <p className="mt-2 text-fg-muted">
                {t('mfa.pasteOrQr')}
                <code className="rounded bg-surface-muted px-1 py-0.5">{setup.secret}</code>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder={t('mfa.tokenInput')}
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-40"
              />
              <Button onClick={activate} disabled={busy || token.length !== 6}>
                {busy ? t('mfa.verifying') : t('mfa.activate')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {backupCodes && (
        <Card className="border-success/40 bg-success/5 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              {t('mfa.activatedTitle')}
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p>{t('mfa.backupCopy')}</p>
            <ul className="grid grid-cols-2 gap-2 font-mono text-xs">
              {backupCodes.map((code) => (
                <li key={code} className="rounded border border-border bg-surface px-2 py-1">
                  {code}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
